import {
  Archive,
  Barcode,
  Bot,
  Boxes,
  ChefHat,
  ChevronsLeft,
  ChevronsRight,
  ClipboardCheck,
  CloudOff,
  EllipsisVertical,
  Eye,
  FileText,
  Hammer,
  History as HistoryIcon,
  Home,
  LogOut,
  Map as MapIcon,
  Menu,
  Network,
  MessageSquarePlus,
  QrCode,
  RefreshCw,
  Search,
  Settings,
  Shovel,
  ShoppingCart,
  Thermometer,
  UploadCloud,
  UserCog,
  Utensils,
  WalletCards,
  X
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { postJson } from "../../lib/api.js";
import { UserAvatar } from "../../components/UserAvatar.js";
import { signOutWithTeardown } from "../../lib/offlineCache.js";
import { AppToast } from "./AppToast.js";
import { useHousehold } from "../context.js";
import { HeaderActionsProvider, headerActionsBadgeTotal, useHeaderActionsRegistry } from "./headerActions.js";
import { HeaderMenu, type HeaderMenuItem } from "./HeaderMenu.js";

export const tabs = [
  { id: "dashboard", label: "Dashboard", path: "/", icon: Home },
  { id: "assistant", label: "Assistant", path: "/assistant", icon: Bot },
  { id: "search", label: "Search", path: "/search", icon: Search },
  { id: "scan", label: "Scan", path: "/scan", icon: QrCode },
  { id: "inventory", label: "Things", path: "/inventory", icon: Boxes },
  { id: "groceries", label: "Food", path: "/groceries", icon: Barcode },
  { id: "chores", label: "Chores", path: "/chores", icon: ClipboardCheck },
  { id: "projects", label: "Projects", path: "/projects", icon: Hammer },
  { id: "floor-plan", label: "Home Map", path: "/floor-plan", icon: MapIcon },
  { id: "garden", label: "Garden", path: "/garden", icon: Shovel },
  { id: "bms", label: "Climate", path: "/bms", icon: Thermometer },
  { id: "network", label: "Network", path: "/network", icon: Network },
  { id: "recipes", label: "Recipes", path: "/recipes", icon: ChefHat },
  { id: "meal-assembler", label: "Meals", path: "/meal-assembler", icon: Utensils },
  { id: "shopping", label: "Shopping", path: "/shopping", icon: ShoppingCart },
  { id: "feedback", label: "Feedback", path: "/feedback", icon: MessageSquarePlus },
  { id: "finance", label: "Finance", path: "/finance", icon: WalletCards },
  { id: "release-notes", label: "Release Notes", path: "/release-notes", icon: FileText },
  // Routable and deep-linkable but deliberately absent from navGroups below
  // (#1039): it is reached from the header profile dropdown, the same way
  // release-notes is reachable without a sidebar item.
  // NOTE: `label` is the ENGLISH SOURCE, not what the shell renders. The array
  // is module-level — evaluated once at import, long before i18next has a
  // language — so it cannot hold translated copy. Display names come from
  // `tabLabelKey` resolved with `t` at each render site (see below).
  { id: "history", label: "History", path: "/history", icon: HistoryIcon },
  { id: "settings", label: "Settings", path: "/settings", icon: Settings }
] as const;

export type Tab = typeof tabs[number]["id"];

/**
 * Translation key for a tab's display name, in the `shell` namespace — the ids
 * are the key names, so `tabs[].label` never has to be matched as a string.
 *
 * Exported because the command palette lists the same pages and has to say the
 * same words: rendering one surface from `tabs[].label` while the sidebar reads
 * this table would put "Finance" and "Finanzas" on the same screen.
 */
export function tabLabelKey(tab: Tab): string {
  return `tabs.${tab}`;
}

const tabsByPath = new Map<string, Tab>(tabs.map((item) => [item.path, item.id]));

export function tabPath(tab: Tab, scanToken = ""): string {
  if (tab === "scan" && scanToken) return `/scan/${encodeURIComponent(scanToken)}`;
  return tabs.find((item) => item.id === tab)?.path ?? "/";
}

export function tabFromPath(pathname: string): Tab | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (normalized.startsWith("/scan/")) return "scan";
  if (normalized.startsWith("/inventory/assets/")) return "inventory";
  if (normalized.startsWith("/inventory/places/")) return "inventory";
  // Foods get a routed detail page too (#1045/#533): a food is a thing you can
  // link to, not just a row in a grid.
  if (normalized.startsWith("/groceries/foods/")) return "groceries";
  if (normalized.startsWith("/recipes/")) return "recipes";
  if (normalized.startsWith("/assistant/")) return "assistant";
  if (normalized === "/release-notes" || normalized.startsWith("/release-notes/")) return "release-notes";
  return tabsByPath.get(normalized) ?? (normalized === "/dashboard" ? "dashboard" : null);
}

export function scanTokenFromPath(pathname: string): string {
  if (!pathname.startsWith("/scan/")) return "";
  const token = pathname.split("/")[2] ?? "";
  try {
    return decodeURIComponent(token);
  } catch {
    return token;
  }
}

function shouldHandleLinkClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey && !event.defaultPrevented;
}

// Stable ids, not copy. The heading is resolved with `t` at render: keying off
// the English label left Spanish showing "Now"/"Home" and, worse, feeding them
// into a translated sentence to produce "Páginas de Now" (#1088).
const navGroups: Array<{ id: string; tabs: Tab[] }> = [
  { id: "now", tabs: ["dashboard", "assistant", "search", "scan", "feedback"] },
  { id: "home", tabs: ["inventory", "floor-plan", "garden", "bms", "network"] },
  { id: "food", tabs: ["groceries", "recipes", "meal-assembler", "shopping"] },
  { id: "work", tabs: ["chores", "projects"] },
  { id: "money", tabs: ["finance", "settings"] }
];

/**
 * Tabs the public demo hides (#838). Both are already inert there — the server's
 * read-only guard rejects an assistant turn and a feedback submission alike — so
 * this only removes doors that lead nowhere. The assistant is the one that would
 * otherwise cost GPU time per visitor, and feedback would aim a public form at
 * the owner's issue tracker.
 */
export const demoHiddenTabs = new Set<Tab>(["assistant", "feedback"]);

const NAV_COLLAPSED_KEY = "household.nav-collapsed";

function readCollapsedPreference(): boolean {
  try {
    return window.localStorage.getItem(NAV_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

type ShellProps = {
  children: ReactNode;
  /** Async so the sign-out button can wait for the offline cache purge (#1003). */
  onSignedOut: () => void | Promise<void>;
  realtimeState?: "connected" | "reconnecting";
  pendingUpdateCount?: number;
  online?: boolean;
  pendingSyncCount?: number;
  /** Attachment files still uploading in the background (#522). */
  pendingUploadCount?: number;
  /** Public read-only demo instance (#839). */
  demoMode?: boolean;
  /**
   * The URL named no page (#1259). The header still needs a heading, and the
   * tab's own name is the wrong one — "Dashboard" above a body that says the
   * page does not exist reads as a broken Dashboard rather than a bad address.
   */
  routeNotFound?: boolean;
  onRetrySync?: () => void;
  onApplyUpdates?: () => void;
  /**
   * Opens the command palette (#1055). App owns that state because the palette
   * is mounted as a sibling of this component, not a child. Optional: when it
   * is absent the search button isn't rendered at all, so the header degrades
   * to exactly what it was before.
   */
  onOpenSearch?: () => void;
};

/**
 * The provider has to sit ABOVE the header, not beside it: the header renders
 * the ⋮ menu and `children` publish into it, so both have to be under the same
 * context. Shell is therefore only the wrapper, and everything that reads the
 * registry lives in ShellFrame below.
 */
export function Shell(props: ShellProps) {
  return (
    <HeaderActionsProvider>
      <ShellFrame {...props} />
    </HeaderActionsProvider>
  );
}

function ShellFrame({
  children,
  onSignedOut,
  realtimeState = "connected",
  pendingUpdateCount = 0,
  online = true,
  pendingSyncCount = 0,
  pendingUploadCount = 0,
  demoMode = false,
  routeNotFound = false,
  onRetrySync,
  onApplyUpdates,
  onOpenSearch
}: ShellProps) {
  const { tab, setTab, user, households, switchHousehold, error, setError, toast, dismissToast, run, scanToken, setScanData } = useHousehold();
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsedPreference);
  const { t } = useTranslation("shell");
  const { actions, invoke } = useHeaderActionsRegistry();
  // One variable rather than a boolean per menu, so "both dropdowns open at
  // once" is not a representable state.
  const [openHeaderMenu, setOpenHeaderMenu] = useState<"page" | "profile" | null>(null);
  // The reconnecting banner is dismissable; the dismissal is per-outage, so it
  // resets once the connection recovers and can reappear on a later, sustained
  // drop (#458).
  const [reconnectDismissed, setReconnectDismissed] = useState(false);
  const headerRef = useRef<HTMLElement | null>(null);
  const activeTab = tabs.find((item) => item.id === tab);

  useEffect(() => {
    if (realtimeState !== "reconnecting") setReconnectDismissed(false);
  }, [realtimeState]);

  // Publish the header's RENDERED height so anything fixed can clear it (#823).
  // Its min-height token is only a floor: the header grows with the household
  // switcher and at the compact breakpoints, and a notice offset from the floor
  // would sit back inside it in exactly those states.
  useEffect(() => {
    const header = headerRef.current;
    if (!header || typeof ResizeObserver === "undefined") return undefined;
    const publish = () => {
      document.documentElement.style.setProperty("--app-header-measured", `${Math.round(header.getBoundingClientRect().height)}px`);
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(header);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--app-header-measured");
    };
  }, []);

  function toggleCollapsed() {
    setCollapsed((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(NAV_COLLAPSED_KEY, next ? "1" : "0");
      } catch {
        // Preference just won't persist.
      }
      return next;
    });
  }

  useEffect(() => {
    if (!menuOpen) return undefined;
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setMenuOpen(false);
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [menuOpen]);

  // The drawer is modal on a phone: the page behind it must not scroll under
  // the finger while it is open (September audit). The class lives on <html>
  // because that is the scrolling element; base.css does the rest.
  useEffect(() => {
    if (!menuOpen) return undefined;
    document.documentElement.classList.add("nav-drawer-open");
    return () => document.documentElement.classList.remove("nav-drawer-open");
  }, [menuOpen]);

  function openTab(nextTab: Tab) {
    setTab(nextTab);
    setMenuOpen(false);
  }

  function openTabLink(event: MouseEvent<HTMLAnchorElement>, nextTab: Tab) {
    if (!shouldHandleLinkClick(event)) return;
    event.preventDefault();
    openTab(nextTab);
  }

  // `onSelect` routes back through the registry by id rather than closing over
  // the handler that was rendered, so a menu item always runs the page's
  // current callback even when the registration was skipped as visually
  // unchanged (see headerActions.tsx).
  const pageActionItems = useMemo<HeaderMenuItem[]>(
    () => actions.map((action) => ({
      id: action.id,
      label: action.label,
      icon: action.icon,
      badge: action.badge,
      disabled: action.disabled,
      title: action.title,
      danger: action.danger,
      onSelect: () => invoke(action.id)
    })),
    [actions, invoke]
  );

  const profileItems = useMemo<HeaderMenuItem[]>(() => [
    {
      id: "edit-account",
      label: t("header.editAccount"),
      icon: UserCog,
      onSelect: () => {
        // Settings routes its own sections by hash (#1057). Assigning
        // `location.hash` would push a SECOND history entry on top of the one
        // setTab just pushed, so Back would appear to do nothing and only then
        // fall back to hashless Settings. replaceState folds the hash into that
        // single entry instead.
        //
        // Both calls run synchronously before React re-renders, so a Settings
        // page mounting for the first time already reads #account. The explicit
        // hashchange covers the other case — Settings is ALREADY mounted (the
        // user was on another of its sections), where replaceState fires no
        // event and the section would otherwise never switch.
        setTab("settings");
        window.history.replaceState(null, "", `${tabPath("settings")}#account`);
        window.dispatchEvent(new Event("hashchange"));
      }
    },
    {
      id: "history",
      label: t("header.history"),
      icon: HistoryIcon,
      onSelect: () => setTab("history")
    },
    {
      id: "log-off",
      label: t("header.logOff"),
      icon: LogOut,
      danger: true,
      // The old Sign out handler, INCLUDING the offline-cache teardown main
      // added while this branch was in review (#1003). Copying the handler as
      // it looked when the branch started would have quietly dropped that: the
      // next person to sign in on this device would inherit the last one's
      // cached data.
      onSelect: () => run(() => signOutWithTeardown(
        () => postJson("/api/v1/auth/logout", {}),
        () => {
          setScanData(null);
          return onSignedOut();
        }
      ), { refresh: false })
    }
  ], [onSignedOut, run, setScanData, setTab, t]);

  return (
    <div className={`app-shell${menuOpen ? " nav-open" : ""}${collapsed ? " nav-collapsed" : ""}`}>
      {/* First in the order, so a keyboard user is not walked through twenty
          sidebar links on every page (September audit). The marketing site
          already had one; the app did not. */}
      <a className="skip-link" href="#main-content">{t("nav.skipToContent")}</a>
      {menuOpen ? <button className="nav-backdrop" type="button" aria-label={t("nav.closeNavigation")} onClick={() => setMenuOpen(false)} /> : null}

      <aside className="side-nav" id="app-navigation-panel">
        <div className="side-nav-head">
          <a
            className="brand"
            href={tabPath("dashboard")}
            aria-label={t("nav.home")}
            onClick={(event) => openTabLink(event, "dashboard")}
          >
            <Archive size={22} />
            <span>{t("brand")}</span>
          </a>
          <button
            className="icon-button nav-collapse-toggle"
            type="button"
            title={collapsed ? t("nav.expand") : t("nav.collapse")}
            aria-label={collapsed ? t("nav.expand") : t("nav.collapse")}
            onClick={toggleCollapsed}
          >
            {collapsed ? <ChevronsRight size={17} /> : <ChevronsLeft size={17} />}
          </button>
          <button
            className="icon-button nav-close"
            type="button"
            aria-label={t("nav.closeNavigation")}
            onClick={() => setMenuOpen(false)}
          >
            <X size={18} />
          </button>
        </div>
        <nav className="page-groups" aria-label={t("nav.pages")}>
          {navGroups.map((group) => {
            const groupLabel = t(`nav.groups.${group.id}`);
            return (
            <section className="page-group" key={group.id} aria-label={t("nav.groupPages", { group: groupLabel })}>
              <p className="nav-group-label">{groupLabel}</p>
              {group.tabs.map((tabId) => {
                if (demoMode && demoHiddenTabs.has(tabId)) return null;
                const item = tabs.find((candidate) => candidate.id === tabId);
                if (!item) return null;
                const Icon = item.icon;
                // The title is the same string as the label on purpose: when
                // the rail is collapsed the label is hidden and the tooltip is
                // the only name of the page, so it cannot stay English.
                const label = t(tabLabelKey(item.id));
                // A URL that matched nothing still carries a tab so the shell
                // has a frame, but that tab is not where the person is: the
                // not-found page marks no item current (September audit).
                const current = !routeNotFound && tab === item.id;
                return (
                  <a
                    key={item.id}
                    href={tabPath(item.id, scanToken)}
                    className={current ? "nav-item active" : "nav-item"}
                    onClick={(event) => openTabLink(event, item.id)}
                    aria-current={current ? "page" : undefined}
                    title={label}
                  >
                    <Icon size={18} />
                    <span>{label}</span>
                  </a>
                );
              })}
            </section>
            );
          })}
        </nav>
      </aside>

      <div className={tab === "assistant" ? "app-main app-main-assistant" : "app-main"}>
        <header className="app-header" ref={headerRef}>
          <div className="header-main">
            <button
              className="icon-button menu-toggle"
              type="button"
              aria-label={menuOpen ? t("nav.close") : t("nav.open")}
              aria-expanded={menuOpen}
              aria-controls="app-navigation-panel"
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? <X size={19} /> : <Menu size={19} />}
            </button>
            <div className="current-page">
              <p className="eyebrow">{user.name}</p>
              <h1>{routeNotFound ? t("notFound.title") : (activeTab ? t(tabLabelKey(activeTab.id)) : null)}</h1>
            </div>
          </div>
          <div className="top-actions">
            {households.length > 1 ? (
              <label className="household-switcher">
                <span className="sr-only">{t("header.activeHousehold")}</span>
                <select
                  value={user.householdId}
                  onChange={(event) => switchHousehold(event.target.value)}
                  aria-label={t("header.activeHousehold")}
                >
                  {households.map((household) => (
                    <option key={household.id} value={household.householdId}>{household.householdName}</option>
                  ))}
                </select>
              </label>
            ) : null}
            {/* Renders nothing at all when the active page published no
                actions — an empty or permanently disabled ⋮ would be worse
                than no ⋮ (#1054). */}
            <HeaderMenu
              id="page-actions"
              open={openHeaderMenu === "page"}
              onOpenChange={(next) => setOpenHeaderMenu(next ? "page" : null)}
              items={pageActionItems}
              triggerLabel={t("header.pageActions")}
              triggerBadge={headerActionsBadgeTotal(actions)}
            >
              <EllipsisVertical size={18} />
            </HeaderMenu>
            {/* Until now the palette was keyboard-only, which meant a touch
                user could not reach it at all (#1055). Placed ahead of the rest
                so the eventual header order is ⋮ → search → avatar (#1038). */}
            {onOpenSearch ? (
              <button
                className="icon-button"
                type="button"
                title={t("header.searchTitle")}
                aria-label={t("header.search")}
                data-palette-return-focus=""
                onClick={onOpenSearch}
              >
                <Search size={18} />
              </button>
            ) : null}
            {/* Share, Refresh and Sign out are gone from here (#1038). The
                first two were never page-aware; Refresh in particular stays
                available through refreshActiveTab() for any page that wants to
                publish it into its own ⋮ menu, which is where a page-level
                refresh belongs. Sign out moves into this dropdown,
                behaviour unchanged. */}
            <HeaderMenu
              id="profile"
              open={openHeaderMenu === "profile"}
              onOpenChange={(next) => setOpenHeaderMenu(next ? "profile" : null)}
              items={profileItems}
              triggerLabel={t("header.accountMenu", { name: user.name })}
              triggerTitle={user.name}
              triggerClassName="icon-button avatar-button"
            >
              <UserAvatar name={user.name} userId={user.id} avatarUrl={user.avatarUrl} size={28} />
            </HeaderMenu>
          </div>
        </header>

        {/* The assistant page is a fixed-height column on compact viewports; the
            workspace itself carries the height there so shell bands (sync
            banner, error alert) shrink the transcript instead of pushing the
            composer off-screen (#457). */}
        <main id="main-content" tabIndex={-1} className={tab === "assistant" ? "workspace workspace-assistant" : "workspace"}>
          {/* Demo notice (#839). Deliberately first and not dismissable: it is
              the standing explanation for why writes don't stick, so it has to
              be on screen when a visitor tries one, not scrolled past earlier. */}
          {demoMode ? (
            <div className="sync-banner demo" role="status">
              <Eye size={15} />
              <span>{t("banner.demo")}</span>
            </div>
          ) : null}
          {/* Offline-first status (#500). Offline takes precedence over the
              realtime "reconnecting" banner below so the two don't contradict:
              while offline the connection is expected to be down. */}
          {!online ? (
            <div className="sync-banner subtle offline" role="status">
              <CloudOff size={15} />
              <span>
                {pendingSyncCount > 0
                  ? t("banner.offlinePending", { count: pendingSyncCount })
                  : t("banner.offline")}
              </span>
            </div>
          ) : pendingSyncCount > 0 ? (
            <div className="sync-banner subtle pending-sync" role="status">
              <UploadCloud size={15} />
              <span>{t("banner.pendingSync", { count: pendingSyncCount })}</span>
              {onRetrySync ? <button type="button" onClick={onRetrySync}>{t("banner.retry")}</button> : null}
            </div>
          ) : null}
          {/* Floated rather than laid out (#823). This one appears on its own
              schedule — whenever another member's write lands — so in normal
              flow it shoved the whole page down under the user's cursor, which
              is worst exactly when they are reaching for a Done/Snooze button.
              The others in this chain follow a deliberate user action (going
              offline, queueing a change) and keep their row. */}
          {online && pendingUpdateCount > 0 ? (
            <div className="sync-banner sync-banner-floating" role="status">
              <RefreshCw size={16} />
              <span>{t("banner.updates", { count: pendingUpdateCount })}</span>
              <button type="button" onClick={onApplyUpdates}>{t("banner.refresh")}</button>
            </div>
          ) : online && realtimeState === "reconnecting" && !reconnectDismissed ? (
            <div className="sync-banner subtle reconnecting" role="status">
              <RefreshCw size={14} />
              <span>{t("banner.reconnecting")}</span>
              <button
                type="button"
                className="sync-banner-dismiss"
                aria-label={t("banner.dismissReconnecting")}
                title={t("common.dismiss")}
                onClick={() => setReconnectDismissed(true)}
              >
                <X size={14} />
              </button>
            </div>
          ) : null}
          {/* Background attachment uploads (#522). Deliberately BELOW the
              reconnecting banner in this chain rather than above it: putting it
              first would hide the #574 reconnect notice for the whole duration
              of an upload, which looks like a fix and is really a mask. */}
          {pendingUploadCount > 0 ? (
            <div className="sync-banner subtle pending-sync" role="status">
              <UploadCloud size={15} />
              <span>{t("banner.uploading", { count: pendingUploadCount })}</span>
            </div>
          ) : null}
          {error ? (
            <div className="alert alert-dismissable" role="alert">
              <span className="alert-message">{error}</span>
              <button
                type="button"
                className="alert-dismiss"
                aria-label={t("banner.dismissError")}
                title={t("common.dismiss")}
                onClick={() => setError("")}
              >
                <X size={14} />
              </button>
            </div>
          ) : null}
          {children}
          {/* Keyed on the toast id so a replacement toast remounts: the dwell
              timer and the marquee measurement both restart from scratch. */}
          {toast ? <AppToast key={toast.id} toast={toast} onDismiss={dismissToast} /> : null}
        </main>
      </div>

    </div>
  );
}
